import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import NotesIcon from "@mui/icons-material/Notes";
import { TextField } from "@mui/material";
import { Product } from "../types/restaurant";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { borderHighlightOptions } from "src/shared/modules/profile/theme/themeConstants";
import { useUserTheme } from "src/shared/utils/useUserTheme";

interface OrderItemProps {
  order: Product;
  editingNote: string | null;
  handleNoteChange: (id: string, note: string) => void;
  handleNoteClick: (id: string) => void;
  handleNoteBlur: (id: string) => void;
  handleIncrement: (item: Product) => void;
  handleDecrement: (item: Product) => void;
  deleteProduct: (id: string) => void;
}

const OrderItem: React.FC<OrderItemProps> = ({ order, editingNote, handleNoteChange, handleNoteClick, handleNoteBlur, handleIncrement, handleDecrement, deleteProduct }) => {
  const { user } = useUser();
  const { bgClass } = useUserTheme();

  const themeKey = user?.background_colour || "DodgerBlue";
  const highlightClass = borderHighlightOptions[themeKey];

  const totalPrice = (Number(order.Amount) * (order.quantity || 0)).toFixed(2);

    // 👇 Responsive truncation logic
    const [truncateLength, setTruncateLength] = useState(window.innerWidth <= 1024 ? 10 : 100);
  
    useEffect(() => {
      const handleResize = () => {
        setTruncateLength(window.innerWidth <= 1024 ? 10 : 25);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
  
    const truncateText = (text: string, length: number) => (text.length > length ? `${text.slice(0, length)}...` : text);

  return (
    <div
      className={`relative grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-xl p-1 mb-2 shadow-md hover:shadow-lg transition-all duration-200 bg-gray-50 border-2 ${highlightClass ? `${highlightClass} ring-1 ring-opacity-40` : "border-transparent"}`}>
      <button
        onClick={() => deleteProduct(order._id.$oid)}
        className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-600 border border-red-200 
          hover:bg-red-600 hover:text-white hover:scale-110 transition-all duration-200 shadow-sm"
      >
        <CloseIcon fontSize="small" />
      </button>

      <div className="flex flex-col justify-center min-w-0">
        <div className="flex flex-col justify-center min-w-0 pl-1">
          <p className="text-sm sm:text-base font-semibold truncate text-gray-800 max-w-[180px] sm:max-w-[260px]">{truncateText(order.ProductName, truncateLength)}</p>
          {order.ArabicName && <p className="text-xs sm:text-sm text-gray-700 truncate leading-tight mt-0.5">{truncateText(order.ArabicName, truncateLength)}</p>}
        </div>

        <div className="mt-0.5">
          {editingNote === order._id.$oid ? (
            <TextField fullWidth size="small" placeholder="Add note..." value={order.notes || ""}
              onChange={(e) => handleNoteChange(order._id.$oid, e.target.value)}
              onBlur={() => handleNoteBlur(order._id.$oid)}
              autoFocus InputProps={{ className: "text-xs h-7" }}
            />
          ) : (
            <button
              onClick={() => handleNoteClick(order._id.$oid)}
              className={`flex items-center gap-1 text-xs text-left transition-colors ${order.notes ? "text-gray-600" : "text-gray-400 hover:text-gray-500"}`}
            >
              <NotesIcon className="text-xs" />
              <span className="truncate flex-1 text-xs">{order.notes || "Add note"}</span>
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4 w-full sm:w-auto">
        <div className="flex items-center gap-2">
          <button className={`${bgClass} rounded-full p-1 hover:opacity-90 transition-all`} onClick={() => handleDecrement(order)} disabled={!order.quantity || order.quantity <= 0}>
            <RemoveIcon fontSize="small" />
          </button>

          <span className="px-3 py-1 rounded text-center min-w-[38px] border border-gray-300 text-sm sm:text-base font-bold bg-white shadow-md">{order.quantity || 0}</span>

          <button className={`${bgClass} rounded-full p-1 hover:opacity-90 transition-all`} onClick={() => handleIncrement(order)}>
            <AddIcon fontSize="small" />
          </button>
        </div>

        <p className="text-[#D70015] font-semibold text-sm sm:text-base text-right whitespace-nowrap">{totalPrice} QAR</p>
      </div>
    </div>
  );
};

export default OrderItem;
